import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { newsService } from '../services/api';
import { NewsItem } from '../types';
import { useLanguage } from '../context/LanguageContext';
import NewsContent from '../components/NewsContent';
import '../styles/News.css';

const News: React.FC = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [expandedNews, setExpandedNews] = useState<number | null>(null);
    const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
    const { t, locale } = useLanguage();
    const dateLocale = locale === 'pt-BR' ? 'pt-BR' : 'en-US';

    useEffect(() => {
        const loadNews = async () => {
            try {
                const data = await newsService.getAllNews();
                setNewsItems(data);
            } catch (err) {
                console.error('Erro ao carregar notícias', err);
            }
        };
        loadNews();
    }, []);

    const categories = useMemo(() => {
        const base = [{ id: 'all', label: t('news.allNews') }];
        const set = new Set<string>();
        newsItems.forEach((n) => {
            if (n.category) set.add(n.category);
        });
        const extra = Array.from(set).map((c) => ({
            id: c,
            label: c.charAt(0).toUpperCase() + c.slice(1)
        }));
        return [...base, ...extra];
    }, [newsItems, t]);

    const filteredNews = activeCategory === 'all'
        ? newsItems
        : newsItems.filter(item => item.category === activeCategory);

    const toggleNews = (id: number) => {
        setExpandedNews(expandedNews === id ? null : id);
    };

    const formatDate = (value?: string) => {
        if (!value) return '';
        const d = new Date(value);
        return d.toLocaleDateString(dateLocale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="news-page">
            <div className="container">
                {/* News / Announcements Section (Accordion Style) */}
                <section className="news-section">
                    <div className="news-header-horizontal">
                        <div className="header-left">
                            <h2>{t('news.title')} <span className="highlight">{t('news.titleHighlight')}</span></h2>
                        </div>
                        <div className="header-right">
                            <div className="news-filters">
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        className={`filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
                                        onClick={() => setActiveCategory(cat.id)}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="news-accordion">
                        {filteredNews.map((news, index) => (
                            <div key={news.id} className={`accordion-item ${index === 0 ? 'active' : ''}`}>
                                <div className="accordion-header" onClick={() => news.id && toggleNews(news.id)}>
                                    <span className="acc-date">{formatDate(news.createdAt)}</span>
                                    <h3 className="acc-title">{news.title}</h3>
                                    <i className={`ri-add-line acc-icon ${expandedNews === news.id ? 'rotated' : ''}`}></i>
                                </div>
                                <div className={`accordion-content ${expandedNews === news.id ? 'expanded' : ''}`}>
                                    <div className="content-inner">
                                        <NewsContent content={news.content || news.summary || ''} className="" />
                                        {news.id && (
                                            <Link to={`/news/${news.id}`} className="btn-text">
                                                {t('news.readDetails')}
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {filteredNews.length === 0 && (
                            <p>{t('news.noNews')}</p>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default News;
